import { forwardRef, useEffect, useMemo } from 'react';
import {
  Avatar,
  List,
  Typography,
  Form,
  Checkbox,
  Row,
  Col,
  Tooltip,
  Space,
  Skeleton
} from 'antd';

import {
  BorderOutlined,
  CheckOutlined,
  CheckSquareOutlined,
  UndoOutlined,
} from '@ant-design/icons';

import { FormAddButton, SectionWrapper } from 'common/components';

import { useGetEnumsByEnumNameClaimsVisiblity } from 'hooks';

const TooltipCheckbox = forwardRef((props, ref) => {
  const { title, checked, onChange, ...restProps } = props;

  return (
    <Tooltip title={title}>
      <Checkbox
        ref={ref}
        checked={checked}
        onChange={onChange}
        {...restProps}
      />
    </Tooltip>
  );
});

const ClaimsVisibility = ({
  formInstance,
  visibleClaims,
  selectedItems = []
}) => {

  const { enums: optionalClaims, isLoading: isLoadingEnums } = useGetEnumsByEnumNameClaimsVisiblity('OptionalClaims');

  const filteredClaims = useMemo(() => {
    return selectedItems
      .filter((item) =>
        item.startsWith('qaSpecification.qaSpecClaimsRequired.')
      )
      .map((item) => item.split('.').pop())
      .reverse();
  }, [selectedItems]);

  const displayNames = useMemo(() => {
    return filteredClaims.map((claimName) => {
      const matchedClaim = optionalClaims.find(
        (item) => item.enumCode === claimName
      );
      return matchedClaim ? matchedClaim.enumDisplayName : claimName;
    });
  }, [filteredClaims, optionalClaims]);

  useEffect(() => {
    if (
      optionalClaims.length === 0 ||
      (!displayNames.length && !visibleClaims.length) ||
      !formInstance
    ) {
      return;
    }
    // Combine both sources and deduplicate
    const allClaimsToCheck = Array.from(
      new Set([...displayNames, ...(visibleClaims || [])])
    );

    const allFormValues = {};

    optionalClaims.forEach((item) => {
      // Set true only if in displayNames or visibleClaims
      allFormValues[item.enumDisplayName] = allClaimsToCheck.includes(
        item.enumDisplayName
      );
    });

    formInstance.setFieldsValue(allFormValues);
  }, [optionalClaims, displayNames, visibleClaims, formInstance]);

  const handleCheckAll = () => {
    const allChecked = {};

    optionalClaims.forEach((item) => {
      allChecked[item.enumDisplayName] = true;
    });

    formInstance.setFieldsValue(allChecked);
  };

  const handleHideAll = () => {
    const allUnchecked = {};

    optionalClaims.forEach((item) => {
      if (!filteredClaims.includes(item.enumCode)) {
        allUnchecked[item.enumDisplayName] = false;
      }
    });

    formInstance.setFieldsValue(allUnchecked);
  };

  const handleReset = () => {
    const allClaimsToCheck = Array.from(
      new Set([...displayNames, ...(visibleClaims || [])])
    );

    const resetValues = {};

    optionalClaims.forEach((item) => {
      resetValues[item.enumDisplayName] = allClaimsToCheck.includes(
        item.enumDisplayName
      );
    });

    formInstance.setFieldsValue(resetValues);
  };

  return (
    <>     
        <div style={{ marginRight: 4, textAlign: 'right' }}>
          <Space>
            <FormAddButton
              text='Reset'
              icon={<UndoOutlined />}
              type='primary'
              onClick={handleReset}
            />
            <FormAddButton
              type='secondary'
              text='Check all'
              icon={<CheckSquareOutlined />}
              onClick={handleCheckAll}
            />
            <FormAddButton
              type='secondary'
              text='Hide all'
              icon={<BorderOutlined />}
              onClick={handleHideAll}
              danger
            />
          </Space>
        </div>
        
          <Skeleton active loading={isLoadingEnums || optionalClaims.length === 0}>
        <div>
          <Form form={formInstance}>
            <SectionWrapper title='Additional Claims' style={{ paddingTop: 6 }}>
              <List
                itemLayout='horizontal'
                split={false}
                dataSource={optionalClaims}
                className='scroller'
                style={{ maxHeight: 480 }}
                renderItem={(item) => (
                  <List.Item
                    style={{ padding: '0', paddingLeft: 18, maxHeight: 36 }}
                    key={item.enumDisplayName}
                  >
                    <List.Item.Meta
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 0,
                      }}
                      avatar={
                        <Avatar
                          className='qas-spec-draggable__data-type'
                          shape='square'
                          icon={<CheckOutlined />}
                        />
                      }
                      title={
                        <Row align='middle'>
                          <Col span={8}>
                            <Typography.Text ellipsis>
                              {item.enumDisplayName}
                            </Typography.Text>
                          </Col>
                          <Col flex='auto' style={{ marginTop: 12 }}>
                            <Form.Item
                              name={item.enumDisplayName}
                              valuePropName='checked'
                              style={{ marginBottom: 0 }}
                            >
                              <TooltipCheckbox
                                title={item.enumDisplayName}
                                disabled={filteredClaims.includes(item.enumCode)} // disable if required claim
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      }
                    />
                  </List.Item>
                )}
              />
            </SectionWrapper>
          </Form>
        </div>
      </Skeleton>
    </>
  );
};

export default ClaimsVisibility;
